import { getApplication, getFooter, withLoading } from 'src/lib/utils';
import { getGameScene } from '../minigame/match2-timestep/game/getGameScene';
import { getRescueLevelTheme, setRescueLevelTheme } from './rescueLevel.theme';
import { AssetGroup } from '@play-co/timestep-core';
import { Feature } from 'src/replicant/analytics';
import { openPopupPromise } from 'src/lib/popups/popupOpenClose';
import StateObserver, { user } from 'src/StateObserver';
import { PuzzleMapImporter } from '../minigame/match2-timestep/game/util/PuzzleMapImporter';
import {
  PuzzleStartData,
  match2SendPuzzleFinishAnalytics,
  match2SendPuzzleStartAnalytics,
} from '../minigame/match2-timestep/match2.analytics';
import { RescuePuzzleFinishArgs } from '../scenes/RescueLevelScene';
import {
  rescueLevelGetActive,
  rescueLevelIsEnabled,
} from 'src/replicant/rescueLevel/rescueLevel.getters';

/**
 * Play the rescue level minigame if available
 */
export async function playRescueLevel(args: {
  origin: 'hud' | 'launch' | 'postLevel' | 'pinch';
}) {
  // Only play if rescue level is enabled
  if (!rescueLevelIsEnabled(user())) return false;

  const active = rescueLevelGetActive(user());
  if (!active) return false;

  setRescueLevelTheme(active.theme);

  // Ensure theme assets are loaded
  await withLoading(
    async () => {
      const assetGroup = new AssetGroup(getRescueLevelTheme().getAssets());
      await assetGroup.load();
    },
    {
      feature: Feature.RESCUE_LEVEL._,
      subFeature: Feature.RESCUE_LEVEL.THEME_ASSETS,
    },
  );

  const { screenManager } = getApplication();
  let keepPlaying = true;
  let retries = 0;

  while (keepPlaying) {
    // Show the play popup
    const play = await openPopupPromise('popupRescueLevelPlay', {
      retry: retries > 0,
      origin: args.origin,
    });

    if (!play) {
      // If the player chooses not to play, complete as skipped
      await StateObserver.invoke.rescueLevelComplete({
        result: 'skip',
      });
      // Force update the footer button
      void getFooter()?.minigameButton.playLevelUp();
      // Terminate the loop, and return true if user played already
      return retries > 0;
    }

    let puzzleStartData: PuzzleStartData | undefined;

    // Start the minigame
    await screenManager.showScreen('minigame', {
      minigame: 'rescue-level',
      levelData: active.levelData,
      timeout: active.timeout,
      onPuzzleStart: () => {
        // Send puzzle start analytics event
        puzzleStartData = sendPuzzleStartEvent();
      },
      onPuzzleFinish: (args: RescuePuzzleFinishArgs) => {
        // Send puzzle finish analytics event
        if (!puzzleStartData) return;
        const timeToCompleteMs = Math.max(
          0,
          args.timeoutMs - args.remainingTimeMs,
        );
        sendPuzzleFinishEvent(puzzleStartData, {
          attempts: retries + 1,
          timeToComplete: Number((timeToCompleteMs / 1000).toFixed(1)),
        });
      },
    });

    await getGameScene()?.waitForComplete();

    // Find out the result of the minigame
    const result = getGameScene()?.getResult();

    // If won, notify the system
    if (result === 'win') {
      await StateObserver.invoke.rescueLevelComplete({
        result,
      });
    }

    // Once the minigame is complete, go back to the pet screen
    await screenManager.showScreen('pet');

    // If the player won, break the loop
    if (result === 'win') {
      // Force update the footer button
      void getFooter()?.minigameButton.playLevelUp();
      // Terminate the loop
      break;
    }

    // After a loss, player can choose to retry
    retries += 1;
  }

  // User played the feature, return true
  return true;
}

/**
 * Launch sequence for rescue levels
 */
export async function rescueLevelLaunch() {
  // Skip if rescue level is not enabled
  if (!rescueLevelIsEnabled(user())) return false;

  // Check if rescue level is active
  const active = rescueLevelGetActive(user());
  if (!active) return false;

  // Offer the player to play the rescue level - return true if played
  return await playRescueLevel({ origin: 'launch' });
}

/**
 * Send the puzzle start analytics event, exclusively for rescue levels
 */
function sendPuzzleStartEvent() {
  const active = rescueLevelGetActive(user());
  if (!active) return undefined;

  const mapDef = new PuzzleMapImporter(active.levelData).import();
  if (!mapDef) return undefined;

  const rescueLevel = user().match2.level;
  const rescueLevelOrder = (user().rescueLevel?.playedLevels?.length ?? 0) + 1;

  return match2SendPuzzleStartAnalytics(
    [],
    {
      ...mapDef,
      levelVersion: 'rescue',
      regionVersion: 'rescue',
      levelHash: 'rescue',
      regionHash: 'rescue',
    },
    rescueLevel,
    {
      rescueLevel,
      rescueTheme: active.theme,
      rescueLevelOrder,
    },
  );
}

/**
 * Send the puzzle finish analytics event, exclusively for rescue levels
 */
function sendPuzzleFinishEvent(
  puzzleStart: PuzzleStartData,
  args: {
    attempts: number;
    timeToComplete: number;
  },
) {
  const active = rescueLevelGetActive(user());
  if (!active) return undefined;

  const results = getGameScene()?.getPuzzleResults();
  if (!results) return;

  const rescueLevel = user().match2.level;
  const rescueLevelOrder = (user().rescueLevel?.playedLevels?.length ?? 0) + 1;

  match2SendPuzzleFinishAnalytics(
    [],
    puzzleStart,
    results,
    rescueLevel,
    args.attempts,
    {
      rescueLevel,
      rescueTheme: active.theme,
      rescueTimeToComplete: args.timeToComplete,
      rescueLevelOrder,
    },
  );
}
